import ee from '../../services/earth-engine';
import { eeEval } from '../utils/index.js';
import { extentOf } from '../../common/utils';
import { addPredDepthStd, getTrainingDepthReducer, observedDepthImage, trainingCollection } from './depthImage';
import { runInSlices } from './trainingSamples';
import {
  depthBinLabel,
  DEPTH_BIN_PROPERTY,
  DEPTH_BIN_SIZE,
  splitMasks,
  TRAIN_FRACTION,
  withDepthBin,
} from './validation';
import { hasTideSeries } from './tideSeries';
import { INTEGER_FIELDS, NO_DECIMAL_FIELDS } from './constants.js';

/**
 * @function pairedResiduals
 * @description Pairs the modelled depth with the surveyed one pixel by pixel and carries the
 * residual on each sample, so every statistic below is one aggregation over the same collection.
 * @param {Object} predicted - The modelled depth, as an `ee.Image` carrying `pred_depth`.
 * @param {Object} observed - The surveyed depth, as an `ee.Image` carrying `or_depth`.
 * @param {Object} region - The area to sample, as an `ee.Geometry`.
 * @param {Number} scale - Pixel size in metres.
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.FeatureCollection` with `diff`, `absdiff`, `sqdiff`, `o` and `o2`.
 */
const pairedResiduals = (predicted, observed, region, scale) => {
  const paired = predicted.addBands(observed).updateMask(predicted.mask().and(observed.mask()));
  const samples = paired.sample({ region, scale, geometries: false, tileScale: 4 });
  return ee
    .FeatureCollection(samples)
    .filter(ee.Filter.notNull(['pred_depth', 'or_depth']))
    .map((feature) => {
      const f = ee.Feature(feature);
      const o = ee.Number(f.get('or_depth'));
      const diff = ee.Number(f.get('pred_depth')).subtract(o);
      return f.setMulti({ diff, absdiff: diff.abs(), sqdiff: diff.pow(2), o, o2: o.pow(2) });
    });
};

/**
 * @function residualSums
 * @description The sums every accuracy statistic is built from, for one part of the survey: the
 * count, the sums of the residual, its absolute value and its square, the sums of the surveyed
 * depth and of its square, the shallowest and deepest surveyed depth, and the same residual sums
 * per depth band. Sums add across parts, so the statistics of the whole survey come out exactly
 * as one request would give them - see {@link combineResidualSums}.
 * @param {Object} residuals - The paired samples, as {@link pairedResiduals} returns them.
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.Dictionary` `{n, sums, min, max, bins}`.
 */
const residualSums = (residuals) => {
  const binned = withDepthBin(residuals.map((feature) => feature.set('one', 1)));
  return ee.Dictionary({
    n: residuals.size(),
    sums: residuals.reduceColumns(ee.Reducer.sum().repeat(5), ['diff', 'absdiff', 'sqdiff', 'o', 'o2']).get('sum'),
    min: residuals.aggregate_min('o'),
    max: residuals.aggregate_max('o'),
    bins: binned
      .reduceColumns({
        reducer: ee.Reducer.sum().repeat(4).group({ groupField: 4, groupName: DEPTH_BIN_PROPERTY }),
        selectors: ['sqdiff', 'absdiff', 'diff', 'one', DEPTH_BIN_PROPERTY],
      })
      .get('groups'),
  });
};

/**
 * @function combineResidualSums
 * @description The accuracy statistics from the sums of every part: RMSE, MAE, bias, R2, scatter
 * index, normalised RMSE and skill score, and the same RMSE, MAE and bias per depth band. Each of
 * the three ratios is null rather than a division by zero when its denominator vanishes, which
 * happens whenever the survey holds a single depth. The standard deviation of the survey is the
 * population one, as Earth Engine's `ee.Reducer.stdDev` gives it.
 * @param {Array<Object>} parts - Evaluated {@link residualSums}, one per part.
 * @param {Number} [binSize] - Band width, in metres.
 * @memberof module:Bathymetry
 * @returns {{measured: Object, bins: Array<Object>}} `measured` as `{n, rmse, mae, bias, r2, SI,
 *  nRMSE, skill}`; `bins` as `{bin, label, n, rmse, mae, bias}`, shallow end first.
 */
export const combineResidualSums = (parts, binSize = DEPTH_BIN_SIZE) => {
  const sums = [0, 0, 0, 0, 0];
  const byBin = new Map();
  let n = 0;
  const [min] = extentOf(parts.map((part) => part?.min).filter((value) => value != null));
  const [, max] = extentOf(parts.map((part) => part?.max).filter((value) => value != null));
  parts.forEach((part) => {
    n += part?.n || 0;
    (part?.sums || []).forEach((value, index) => {
      sums[index] += value || 0;
    });
    (part?.bins || []).forEach((group) => {
      const bin = group[DEPTH_BIN_PROPERTY];
      const total = byBin.get(bin) || [0, 0, 0, 0];
      byBin.set(
        bin,
        total.map((value, index) => value + (group.sum?.[index] || 0)),
      );
    });
  });
  const bins = [...byBin.entries()]
    .sort(([a], [b]) => a - b)
    .map(([bin, [squared, absolute, signed, count]]) => ({
      bin,
      label: depthBinLabel(bin, binSize),
      n: count,
      rmse: count ? Math.sqrt(squared / count) : null,
      mae: count ? absolute / count : null,
      bias: count ? signed / count : null,
    }));
  if (!n)
    return {
      measured: { n: 0, rmse: null, mae: null, bias: null, r2: null, SI: null, nRMSE: null, skill: null },
      bins,
    };
  const [sumDiff, sumAbs, sumSq, sumO, sumO2] = sums;
  const rmse = Math.sqrt(sumSq / n);
  const meanO = sumO / n;
  const ssTot = sumO2 - n * meanO * meanO;
  const stdObs = Math.sqrt(Math.max(0, sumO2 / n - meanO * meanO));
  const zRange = max - min;
  return {
    measured: {
      n,
      rmse,
      mae: sumAbs / n,
      bias: sumDiff / n,
      r2: ssTot === 0 ? null : 1 - sumSq / ssTot,
      SI: meanO === 0 ? null : rmse / meanO,
      nRMSE: zRange === 0 ? null : rmse / zRange,
      skill: stdObs === 0 ? null : 1 - rmse / stdObs,
    },
    bins,
  };
};

/**
 * @function sliceRegion
 * @description The bounding box of a slice of soundings, widened by two pixels so the pixel a
 * sounding on the edge falls in is still sampled.
 * @param {Array<Array<Number>>} slice - `[x, y, depth]` rows.
 * @param {String} [crs] - The CRS of `x` and `y`; WGS84 when it names EPSG:4326 or is missing.
 * @param {Number} scale - Pixel size in metres.
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.Geometry.Rectangle`.
 */
const sliceRegion = (slice, crs, scale) => {
  const projected = crs && crs.indexOf('4326') === -1;
  const pad = projected ? 2 * scale : (2 * scale) / 111320;
  const [xmin, xmax] = extentOf(slice.map((row) => row[0]));
  const [ymin, ymax] = extentOf(slice.map((row) => row[1]));
  return ee.Geometry.Rectangle([xmin - pad, ymin - pad, xmax + pad, ymax + pad], projected ? crs : 'EPSG:4326', false);
};

/**
 * @function computeDepthMetrics
 * @description Scores the modelled depth against the survey.
 * When the validation split is on, the survey is masked to the pixels the model was not fitted
 * on, so the numbers describe prediction rather than recall - see {@link splitMasks}. The same
 * residuals are also reported per 2 m band, which is where a model that works in shallow water
 * and fails in deep water stops looking average; a run that already cross-validated reports the
 * bands from those folds instead, so they are not recomputed here.
 * The survey is scored a slice at a time, each slice rasterised alone and sampled only over its
 * own bounding box - see {@link runInSlices}. In one request, rasterising the 175 908 points of a
 * 50 m grid over the whole Babitonga bay passed Earth Engine's five-minute limit: the points come
 * from a list decoded on the server, with no spatial index, so every tile read all of them. A
 * pixel holding soundings from two slices is scored once per slice; on a grid sparser than the
 * pixels, which is what a survey grid is, none does.
 * @param {Object} params
 * @param {Object} params.image - The modelled depth, as an `ee.Image`.
 * @param {Object} params.region - The area of interest, as an `ee.Geometry`.
 * @param {Object} params.data - The analysis configuration.
 * @param {Object} params.fileData - The uploaded files' state.
 * @param {Object} params.missionInfo - The mission, for its name and its optical resolution.
 * @param {String} params.type - The imagery's processing level, TOA or SR.
 * @param {Object} [params.metricsCoefDict] - What the model fitting already reported, carried into
 *  the same table.
 * **Called by:
 * @see {ProcessingPage} - Bathymetry's result page, at the end of a run.
 * @memberof module:Bathymetry
 * @returns {Promise<Object|null>} `{metrics, bins}`, or null when the run is not scoreable.
 */
export const computeDepthMetrics = async ({ image, region, data, fileData, missionInfo, type, metricsCoefDict }) => {
  if (!image || !region || !missionInfo?.opticalResolution) return null;
  const scale = missionInfo.opticalResolution;
  const reducer = getTrainingDepthReducer(data?.trainingDataReducer?.toLowerCase());
  const validationMask = data?.validationSplit
    ? splitMasks({ scale, crs: fileData.coordinatesSystem }).validation
    : null;
  const predicted = addPredDepthStd(ee.Image(image)).select('pred_depth').toFloat();
  const score = (points, sampledRegion) => {
    const observedAll = observedDepthImage(points, reducer, region);
    const observed = validationMask ? observedAll.updateMask(validationMask) : observedAll;
    return eeEval(residualSums(pairedResiduals(predicted, observed, sampledRegion, scale)));
  };
  const matrix = fileData.trainingMatrix;
  const crs = fileData.coordinatesSystem;
  const tide = hasTideSeries(fileData, data)
    ? eeEval(
        ee.Dictionary({
          tideLevel_m: image.get('tideValue'),
          dateGauge: image.get('dateGauge'),
          dateImage: image.get('dateImage'),
        }),
      )
    : Promise.resolve({});

  const [parts, tideFields] = await Promise.all([
    matrix?.length
      ? runInSlices(matrix, (slice) => score(trainingCollection(slice, crs), sliceRegion(slice, crs, scale)))
      : score(fileData.matrix, region).then((part) => [part]),
    tide,
  ]).catch((error) => {
    console.error('[computeDepthMetrics] erro no evaluate:', error);
    throw error;
  });
  const combined = combineResidualSums(parts);
  const measured = { ...combined.measured, ...tideFields };
  const bins = metricsCoefDict?.CV_N == null ? combined.bins : undefined;

  return {
    bins,
    metrics: {
      BDS_MODEL: data.modelSelected,
      MISSION: missionInfo?.name ?? '—',
      IMAGE_SOURCE: type,
      RUN_MODE: data.typeImageProcess || 'SINGLE',
      SCALE_M: scale,
      CRS: fileData.coordinatesSystem,
      targetDate: data.imageDate || null,
      ...measured,
      ...(!data.presetMode && { TRAININGDATA_REDUCER: data?.trainingDataReducer || 'median' }),
      ...(data?.validationSplit && {
        VALIDATION_SPLIT: `${Math.round(TRAIN_FRACTION * 100)}/${Math.round((1 - TRAIN_FRACTION) * 100)}`,
      }),
      ...metricsCoefDict,
    },
  };
};

/**
 * @function formatMetricValue
 * @description A metric as the panel and the report show it: categorical fields untouched, counts
 * and scales rounded, every other number to three decimals.
 * @param {String} key - The field name.
 * @param {Number|String} value - Its value.
 * @memberof module:Bathymetry
 * @returns {String|Number} The value, formatted.
 */
export const formatMetricValue = (key, value) => {
  if (NO_DECIMAL_FIELDS.has(key)) return value;
  if (INTEGER_FIELDS.has(key)) return typeof value === 'number' ? String(Math.round(value)) : value;
  if (typeof value === 'number') return value.toFixed(3);
  return value;
};

/**
 * @function metricsCsv
 * @description The metrics table as a CSV: one header and one row for the run, then the per-band
 * breakdown below it, separated by a blank line so a spreadsheet reads them as two tables.
 * @param {Object} report - The metrics, as {@link computeDepthMetrics} returns them.
 * @param {Array<Object>} [bins] - The per-band breakdown.
 * @memberof module:Bathymetry
 * @returns {String} The CSV.
 */
export const metricsCsv = (report, bins = []) => {
  const header = Object.keys(report).join(',');
  const row = Object.values(report)
    .map((value) => (value != null ? value : ''))
    .join(',');
  const binRows = bins.length
    ? [
        '',
        'DEPTH_BAND,n,rmse,mae,bias',
        ...bins.map((bin) =>
          [bin.label, bin.n, bin.rmse, bin.mae, bin.bias].map((value) => (value != null ? value : '')).join(','),
        ),
      ]
    : [];
  return [header, row, ...binRows].join('\n');
};
